import React, { useState, useEffect, useRef } from "react";
import { T } from "./theme";
import { Btn, Input, Sel } from "./ui";
import { supabase, isSupabaseReady } from "./supabase";

function detectarTipoPix(valor) {
  const num = (valor || "").replace(/\D/g, "");
  if (num.length === 14) return "CNPJ";
  if (num.length === 11) return "CPF";
  return null;
}

// ═══════════════════════════════════════════════════════════════════════
// CREATE ACCOUNT — Página pública de cadastro de novos corretores
// Lê ?ref=<tag_unica> da URL pra vincular indicador
// Cria corretor com ativo=false (admin precisa aprovar)
// ═══════════════════════════════════════════════════════════════════════

const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 24);
}

function randStr(n = 4) {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let s = "";
  for (let i = 0; i < n; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

export default function CreateAccount() {
  const [refSlug, setRefSlug] = useState("");
  const [indicador, setIndicador] = useState(null);
  const [form, setForm] = useState({
    nome: "", username: "", email: "", telefone: "",
    cidade: "", uf: "", sexo: "F",
    cpf: "", cnpjCpf: "", tipoPix: "CPF",
    chavePix: "", bancoPix: "", nomePix: "",
    senha: "", senhaConfirma: "",
  });
  const [userCheck, setUserCheck] = useState(null);
  const [foto, setFoto] = useState(null); // File obj
  const [fotoPreview, setFotoPreview] = useState(null);
  const fotoRef = useRef(null);
  const [aceitouTermos, setAceitouTermos] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sucesso, setSucesso] = useState(false);

  // Lê ?ref= da URL
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const ref = sp.get("ref");
    if (ref) setRefSlug(slugify(ref));
  }, []);

  // Busca indicador no Supabase
  useEffect(() => {
    if (!refSlug || !isSupabaseReady) return;
    (async () => {
      const { data } = await supabase
        .from("corretores")
        .select("id, nome, tag_unica")
        .ilike("tag_unica", refSlug)
        .maybeSingle();
      if (data) setIndicador(data);
    })();
  }, [refSlug]);

  function up(k, v) { setForm(prev => ({ ...prev, [k]: v })); }

  // Debounced check de username único
  useEffect(() => {
    const u = slugify(form.username);
    if (u.length < 3) { setUserCheck(null); return; }
    setUserCheck("checking");
    const t = setTimeout(async () => {
      if (!isSupabaseReady) { setUserCheck(null); return; }
      const { data: c } = await supabase.from("corretores").select("id").ilike("tag_unica", u).maybeSingle();
      const { data: u2 } = await supabase.from("usuarios").select("id").ilike("username", u).maybeSingle();
      setUserCheck(c || u2 ? "taken" : "ok");
    }, 350);
    return () => clearTimeout(t);
  }, [form.username]);

  async function submit() {
    setMsg(null);
    if (!form.nome.trim())     return setMsg({ tipo: "err", texto: "Informe seu nome completo." });
    if (!form.email.trim())    return setMsg({ tipo: "err", texto: "E-mail é obrigatório." });
    if (!form.telefone.trim()) return setMsg({ tipo: "err", texto: "WhatsApp é obrigatório." });
    if (!form.cidade.trim())   return setMsg({ tipo: "err", texto: "Cidade é obrigatória." });
    if (!form.uf)              return setMsg({ tipo: "err", texto: "Estado (UF) é obrigatório." });
    if (!form.cpf.trim())      return setMsg({ tipo: "err", texto: "CPF é obrigatório." });
    if (!form.chavePix.trim()) return setMsg({ tipo: "err", texto: "Chave Pix é obrigatória." });
    if (form.senha.length < 6) return setMsg({ tipo: "err", texto: "Senha mínimo 6 caracteres." });
    if (form.senha !== form.senhaConfirma) return setMsg({ tipo: "err", texto: "Senhas não coincidem." });
    if (!aceitouTermos) return setMsg({ tipo: "err", texto: "Você precisa aceitar os Termos e Condições." });

    const username = slugify(form.username);
    if (username.length < 3) return setMsg({ tipo: "err", texto: "Nome de usuário (mín 3 caracteres, só a-z, 0-9, - e _)." });
    if (userCheck === "taken")  return setMsg({ tipo: "err", texto: "Esse @username já está em uso. Escolha outro." });

    if (!isSupabaseReady) return setMsg({ tipo: "err", texto: "Supabase indisponível. Tente mais tarde." });

    setSalvando(true);
    try {
      const tagUnica = username;
      const tipoEmpresa = form.tipoPix || "CPF";
      const cnpjValor = tipoEmpresa === "CNPJ" ? form.cnpjCpf.trim() : null;

      const insertPayload = {
        nome: form.nome.trim(),
        email: form.email.trim(),
        telefone: form.telefone.trim(),
        cidade: form.cidade.trim(),
        uf: form.uf,
        cpf: form.cpf.trim(),
        sexo: form.sexo || "F",
        cnpj: cnpjValor,
        tipo_empresa: tipoEmpresa,
        chave_pix: form.chavePix.trim(),
        banco_pix: form.bancoPix.trim(),
        nome_pix: form.nomePix.trim() || form.nome.trim(),
        tag_unica: tagUnica,
        ativo: false,
        tipo: "corretor",
        role: "corretor",
        indicado_por: indicador?.id || null,
      };

      const { data: novoCorretor, error: errC } = await supabase
        .from("corretores")
        .insert(insertPayload)
        .select()
        .single();
      if (errC) throw errC;

      const modulesDefault = ["dashboard","ranking","financeiro","times","regional","nacional","rede","meusettings"];
      const { error: errU } = await supabase.from("usuarios").insert({
        username,
        nome: form.nome.trim(),
        password: form.senha,
        role: "corretor",
        corretor_id: novoCorretor.id,
        modules: modulesDefault,
      });
      if (errU) {
        console.error("[CreateAccount] usuarios insert:", errU.message);
        throw new Error("Falha ao criar login: " + errU.message);
      }

      // Upload da foto se selecionada
      if (foto && novoCorretor?.id) {
        try {
          const ext = (foto.name.split(".").pop() || "jpg").toLowerCase();
          const path = `corretores/${novoCorretor.id}/${Date.now()}.${ext}`;
          await supabase.storage.from("avatars").upload(path, foto, { contentType: foto.type, upsert: true });
          const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
          await supabase.from("corretores").update({ foto_url: pub.publicUrl }).eq("id", novoCorretor.id);
        } catch (e) {
          console.warn("[CreateAccount] upload foto:", e.message);
        }
      }

      setSucesso(true);
    } catch (e) {
      setMsg({ tipo: "err", texto: "Erro ao cadastrar: " + e.message });
    } finally {
      setSalvando(false);
    }
  }

  if (sucesso) {
    return (
      <Layout>
        <div style={{
          textAlign: "center", padding: 40,
          background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.xl,
        }}>
          <div style={{ fontSize: 56 }}>✅</div>
          <div className="serif" style={{ fontSize: 24, fontWeight: 600, color: T.brand, marginTop: 12 }}>
            Cadastro recebido!
          </div>
          <div style={{ fontSize: 13, color: T.textSub, marginTop: 14, lineHeight: 1.5 }}>
            Seu cadastro foi enviado para análise.<br/>
            A equipe Bellanor vai aprovar sua conta em até <strong>2 dias úteis</strong> e você receberá um e-mail.<br/><br/>
            Enquanto isso, fique de olho no WhatsApp <strong>{form.telefone}</strong>.
          </div>
          <Btn onClick={() => window.location.href = "/"} style={{ marginTop: 24, justifyContent: "center" }}>
            Ir para o login
          </Btn>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.xl,
        padding: 28, display: "flex", flexDirection: "column", gap: 18,
      }}>
        <div>
          <div className="serif" style={{ fontSize: 28, fontWeight: 600, color: T.brand, textAlign: "center" }}>
            Quero Ser Corretor(a) da Bellanor
          </div>
          <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>
            Preencha seus dados. Sua conta será ativada após análise.
          </div>
        </div>

        {indicador && (
          <div style={{
            padding: "10px 14px", borderRadius: T.r.md,
            background: T.brand + "15", border: `1px solid ${T.brand}44`,
            fontSize: 12, color: T.brand,
          }}>
            🎁 Você está sendo indicado(a) por <strong>{indicador.nome}</strong>
          </div>
        )}
        {refSlug && !indicador && (
          <div style={{
            padding: "8px 12px", borderRadius: T.r.md,
            background: T.warn + "15", border: `1px solid ${T.warn}44`,
            fontSize: 11, color: T.warn,
          }}>
            ⚠️ Link de indicação <code>{refSlug}</code> não encontrado. Cadastro segue sem vínculo.
          </div>
        )}

        {/* Foto de perfil */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 0", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ position: "relative" }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              background: fotoPreview ? `url(${fotoPreview}) center/cover` : T.card,
              border: `2px solid ${T.brand}44`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: T.textDim, fontSize: 28,
            }}>{!fotoPreview && "👤"}</div>
            <button
              type="button"
              onClick={() => fotoRef.current?.click()}
              style={{
                position: "absolute", bottom: -2, right: -2,
                width: 30, height: 30, borderRadius: "50%",
                background: T.brand, color: T.textInv,
                border: `2px solid ${T.bg}`, cursor: "pointer",
                fontSize: 14, fontFamily: "inherit",
              }}
            >📷</button>
            <input
              ref={fotoRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFoto(f);
                setFotoPreview(URL.createObjectURL(f));
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: T.textSub }}>
            <strong>Foto de perfil</strong>
            <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>Opcional · JPG/PNG/WebP · máx 5 MB</div>
          </div>
        </div>

        <Bloco titulo="👤 Dados pessoais">
          <div style={{ gridColumn: "1 / -1" }}>
            <Input label="Nome e sobrenome*" value={form.nome} onChange={e => up("nome", e.target.value)} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ fontSize: 10, color: "#888", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Nome de usuário (@) — vai ser seu login e seu link de indicação*</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "#888", fontSize: 18, fontWeight: 600 }}>@</span>
              <Input
                value={form.username}
                onChange={e => up("username", slugify(e.target.value))}
                placeholder="ex: gilzao"
                style={{ flex: 1 }}
              />
            </div>
            {userCheck === "checking" && <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>verificando…</div>}
            {userCheck === "ok" && <div style={{ fontSize: 10, color: "#4ade80", marginTop: 4 }}>✅ disponível</div>}
            {userCheck === "taken" && <div style={{ fontSize: 10, color: "#f47260", marginTop: 4 }}>❌ já está em uso</div>}
            {form.username && form.username.length < 3 && <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>mínimo 3 caracteres</div>}
          </div>
          <Input label="E-mail*" type="email" value={form.email} onChange={e => up("email", e.target.value)} />
          <Input label="WhatsApp*" value={form.telefone} onChange={e => up("telefone", e.target.value)} placeholder="(85) 99999-9999" />
          <Input label="Cidade*" value={form.cidade} onChange={e => up("cidade", e.target.value)} />
          <Sel label="UF*" value={form.uf} onChange={e => up("uf", e.target.value)}>
            <option value="">—</option>
            {UFS.map(u => <option key={u} value={u}>{u}</option>)}
          </Sel>
          <Input label="CPF*" value={form.cpf} onChange={e => up("cpf", e.target.value)} placeholder="000.000.000-00" />
          <Sel label="Você é*" value={form.sexo} onChange={e => up("sexo", e.target.value)}>
            <option value="F">Corretora (feminino)</option>
            <option value="M">Corretor (masculino)</option>
            <option value="O">Outro</option>
            <option value="N">Prefiro não dizer</option>
          </Sel>
        </Bloco>

        <Bloco titulo="💳 Dados bancários">
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Documento</div>
              <div style={{ display: "flex", gap: 4 }}>
                {["CPF", "CNPJ"].map(tipo => (
                  <button
                    key={tipo}
                    type="button"
                    onClick={() => up("tipoPix", tipo)}
                    style={{
                      padding: "4px 12px", borderRadius: 100,
                      background: form.tipoPix === tipo ? T.brand : "transparent",
                      color: form.tipoPix === tipo ? T.textInv : T.textSub,
                      border: `1px solid ${form.tipoPix === tipo ? T.brand : T.borderMid}`,
                      cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "inherit",
                    }}
                  >{tipo}</button>
                ))}
              </div>
            </div>
            <Input
              value={form.cnpjCpf}
              onChange={e => up("cnpjCpf", e.target.value)}
              placeholder={form.tipoPix === "CNPJ" ? "00.000.000/0000-00" : "000.000.000-00"}
            />
          </div>
          <Input label="Nome do titular da conta" value={form.nomePix} onChange={e => up("nomePix", e.target.value)} placeholder="Mesmo do CPF/CNPJ" />
          <Input label="Banco" value={form.bancoPix} onChange={e => up("bancoPix", e.target.value)} placeholder="Nubank, Bradesco..." />
          <div style={{ gridColumn: "1 / -1" }}>
            <Input label="Chave Pix*" value={form.chavePix} onChange={e => up("chavePix", e.target.value)} placeholder="CPF, e-mail, telefone ou chave aleatória" />
          </div>
        </Bloco>

        <Bloco titulo="🔒 Senha de acesso">
          <Input label="Senha (mín 6)*" type="password" value={form.senha} onChange={e => up("senha", e.target.value)} />
          <Input label="Confirmar senha*" type="password" value={form.senhaConfirma} onChange={e => up("senhaConfirma", e.target.value)} />
        </Bloco>

        {msg && (
          <div style={{
            padding: "10px 14px", borderRadius: T.r.md,
            background: msg.tipo === "ok" ? T.ok + "15" : T.err + "15",
            border: `1px solid ${msg.tipo === "ok" ? T.ok : T.err}44`,
            color: msg.tipo === "ok" ? T.ok : T.err, fontSize: 12,
          }}>{msg.texto}</div>
        )}

        {/* Termos e condições */}
        <label style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          padding: "12px 14px", borderRadius: T.r.md,
          background: aceitouTermos ? T.brand + "15" : T.cardSoft,
          border: `1px solid ${aceitouTermos ? T.brand + "55" : T.border}`,
          cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={aceitouTermos}
            onChange={e => setAceitouTermos(e.target.checked)}
            style={{ marginTop: 3, width: 18, height: 18, cursor: "pointer", accentColor: T.brand }}
          />
          <div style={{ fontSize: 12, color: T.text, lineHeight: 1.5 }}>
            Eu concordo com os{" "}
            <a
              href="/regulamento"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: T.brand, fontWeight: 600, textDecoration: "underline" }}
            >Termos e Condições do Programa Bellanor Alta Performance</a>
          </div>
        </label>

        <Btn onClick={submit} disabled={salvando || !aceitouTermos} style={{ justifyContent: "center", padding: 14 }}>
          {salvando ? "Enviando..." : "🚀 Enviar cadastro"}
        </Btn>

        <div style={{ textAlign: "center", fontSize: 11, color: T.textSub }}>
          Já tem conta? <a href="/" style={{ color: T.brand, fontWeight: 600 }}>Voltar para login</a>
        </div>
      </div>
    </Layout>
  );
}

function Layout({ children }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: T.gradient?.bgRadial || T.bg,
      padding: "32px 16px",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
        <img src="/logo-bellanor.png" alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
             onError={e => { e.target.style.display = "none"; }} />
        <div className="serif" style={{ fontSize: 24, fontWeight: 600, color: T.brand, letterSpacing: 0.5, textTransform: "uppercase" }}>BELLANOR</div>
        <div style={{ width: 1, height: 18, background: T.borderMid, opacity: 0.6 }} />
        <div style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: T.gold, fontWeight: 700 }}>Alta Performance</div>
      </div>
      <div style={{ width: "100%", maxWidth: 560 }}>{children}</div>
    </div>
  );
}

function Bloco({ titulo, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 10 }}>{titulo}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>
    </div>
  );
}
